import React, { useState, useEffect, useCallback } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  Grid,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import { EditOutlined, DeleteOutline, RestartAltOutlined } from "@mui/icons-material";
import _ from "lodash";
import { useFormik } from "formik";
import * as yup from "yup";

//Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";

//Services
import LocationApi from "Services/Location";
import countryCodeData from "./data/index.json";
import MapContainer from "./GoogleMaps";
import GoogleMapLoader from "../../../../Googlemaploader";

const UpdateLocationModal = (props) => {
  const { open, handleClose, locationId } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [details, setDetails] = useState(null);

  // Maps states
  const [shapesDeleted, setShapesDeleted] = useState(0);
  const [polygonData, setPolygonData] = useState([]);

  const getLocationDetails = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        setDetails(res?.data?.data);
      } else {
        setDetails(null);
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Failed to fetch data",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };
    if (locationId) {
      LocationApi.getServiceAreaById(locationId).then(onSuccess, onFailure);
    }
  }, [locationId, dispatch]);

  useEffect(() => {
    if (open) {
      getLocationDetails();
    }
  }, [open, getLocationDetails]);

  //Maps
  const handleDeleteShapes = () => {
    setShapesDeleted((prevCount) => prevCount + 1);
  };

  const handlePolygonChange = (newPolygonData) => {
    setPolygonData(newPolygonData);
  };

  const handleReset = () => {
    getLocationDetails();
  };

  const handleUpdateLocations = (formValues) => {
    dispatch(showLoader("Loading please wait..."));
    const data = {
      locationName: formValues?.locationName,
      countryId: formValues?.country,
      polygonData: polygonData,
    };
    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "Updated successfully",
            autoHideDuration: 3000,
            anchorOrigin: {
              vertical: "top",
              horizontal: "right",
            },
            variant: "success",
          })
        );
        formik?.resetForm();
        setDetails(null);
        getLocationDetails();
        handleClose();
      }
    };
    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while updating",
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "error",
        })
      );
    };

    if (polygonData && polygonData.length > 0) {
      LocationApi.updateServiceArea(locationId, { ...data }).then(onSuccess, onFailure);
    } else {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: "Location data cannot be empty",
          autoHideDuration: 6000,
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          variant: "warning",
        })
      );
    }
  };

  const formik = useFormik({
    initialValues: {
      locationName: "",
      country: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      locationName: yup
        .string()
        .required("Location Name is required")
        .trim()
        .min(3, "Name must be at least 3 letters")
        .max(50, "Max 50 characters are allowed")
        .nullable(),
    }),
    onSubmit: handleUpdateLocations,
  });

  useEffect(() => {
    if (!_.isEmpty(details)) {
      formik.setFieldValue("locationName", details?.locationName || "");
      formik.setFieldValue("country", details?.countryId || "");
      setPolygonData(details?.polygonData || []);
    }
  }, [details]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        formik.resetForm();
        handleClose();
      }}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="lg"
      fullWidth
    >
      <Box className={classes.headerContainer}>
        <Typography className={classes.dialogTitle}>Update Location</Typography>
        <Typography className={classes.breadcrumb}>
          Admin / Zone Management / <span>Location</span>
        </Typography>
      </Box>

      <DialogContent style={{ padding: 0 }}>
        <Grid container spacing={3}>
          {/* Left Column - Form Fields */}
          <Grid item xs={12} md={4}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography className={classes.formLabel}>Location Name</Typography>
                  <TextField
                    name="locationName"
                    placeholder="Enter location name"
                    value={formik.values.locationName}
                    onChange={formik.handleChange}
                    error={formik.touched.locationName && Boolean(formik.errors.locationName)}
                    helperText={formik.touched.locationName && formik.errors.locationName}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.formInput}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography className={classes.formLabel}>Country</Typography>
                  <TextField
                    select
                    name="country"
                    value={formik.values.country}
                    onChange={formik.handleChange}
                    error={formik.touched.country && Boolean(formik.errors.country)}
                    helperText={formik.touched.country && formik.errors.country}
                    fullWidth
                    variant="outlined"
                    size="small"
                    className={classes.formInput}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) => value || <span style={{ color: "#94a3b8" }}>Select Country</span>
                    }}
                  >
                    <MenuItem value="" disabled>Select Country</MenuItem>
                    {countryCodeData.map((country) => (
                      <MenuItem key={country.code} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Box className={classes.dialogActions} style={{ marginTop: "16px" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      className={classes.submitButton}
                    >
                      <EditOutlined style={{ fontSize: "18px" }} />
                      Update Location
                    </Button>
                    <Button
                      className={classes.cancelButton}
                      onClick={() => {
                        formik.resetForm();
                        handleClose();
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Grid>

          {/* Right Column - Map */}
          <Grid item xs={12} md={8}>
            <Box style={{ height: "450px", borderRadius: "12px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
              <GoogleMapLoader>
                <MapContainer
                  showDeleteButton={true}
                  onDeleteShape={handleDeleteShapes}
                  onPolygonChange={handlePolygonChange}
                  initialPolygonData={polygonData}
                  shapesDeleted={shapesDeleted}
                />
              </GoogleMapLoader>
            </Box>

            <Box style={{ display: "flex", gap: "16px", marginTop: "16px" }}>
              <Button
                variant="outlined"
                onClick={handleDeleteShapes}
                startIcon={<DeleteOutline />}
                style={{ color: "#ef4444", borderColor: "#fca5a5", textTransform: "none" }}
              >
                Delete Shapes
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RestartAltOutlined />}
                style={{ color: "#64748b", borderColor: "#cbd5e1", textTransform: "none" }}
              >
                Reset Map
              </Button>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateLocationModal;
